import { Controller } from "@hotwired/stimulus"
import consumer, { releaseCableIfIdle } from "../channels/consumer"

// FD-537: how long a tab may stay hidden before we drop its presence /cable socket.
const HIDDEN_GRACE_MS = 10000

// FD-69: real-time editing presence (MVP). Subscribes to DocumentPresenceChannel for
// the page/wiki being edited and renders three things, all progressive enhancement on
// top of the existing block editor:
//   • an avatar roster of everyone currently editing (the "bar" target),
//   • a live remote caret for each other editor inside the editing surface,
//   • a banner warning that edits aren't merged ("N other people are editing… your
//     save will overwrite theirs") — because this MVP is still last-write-wins.
//
// Roster is peer-maintained: the server relays join/announce/leave/cursor and hands us
// our own identity in a `welcome` message; we ignore our own echoes by sid.
export default class extends Controller {
  static targets = ["bar", "banner"]
  static values = { docType: String, docId: String, collab: Boolean }

  connect() {
    this.me = null
    this.members = new Map() // sid -> member (others only; never ourselves)
    this.carets = new Map() // sid -> caret element
    this.surface = this.element.querySelector(".fd-block-surface")
    this.suspended = false // FD-537: true while the socket is dropped for a hidden tab

    this.subscribe()

    // FD-537: a hidden tab keeps its /cable socket open, pinning a request-billed Cloud
    // Run instance. Drop presence after a grace period once hidden; re-subscribe (which
    // replays the join/announce handshake to rebuild the roster) when visible again.
    this.onVisibilityChange = () => this.handleVisibility()
    document.addEventListener("visibilitychange", this.onVisibilityChange)

    // FD-267: in collaborative mode CollaborationCaret draws the carets and edits merge
    // conflict-free, so this controller is roster-only — no cursor relay, no
    // "your save may overwrite theirs" banner (which is no longer true).
    if (!this.collabValue) {
      this.onSelectionChange = () => this.scheduleCursor()
      document.addEventListener("selectionchange", this.onSelectionChange)
      this.surface?.addEventListener("input", this.onSelectionChange)
    }
    this.render()
  }

  disconnect() {
    document.removeEventListener("selectionchange", this.onSelectionChange)
    this.surface?.removeEventListener("input", this.onSelectionChange)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    if (this.cursorTimer) clearTimeout(this.cursorTimer)
    if (this.suspendTimer) clearTimeout(this.suspendTimer)
    this.subscription?.unsubscribe()
    this.subscription = null
    this.clearRoster()
  }

  subscribe() {
    this.subscription = consumer.subscriptions.create(
      { channel: "DocumentPresenceChannel", doc_type: this.docTypeValue, doc_id: this.docIdValue },
      { received: (data) => this.received(data) }
    )
  }

  // ---- hidden-tab suspend/resume (FD-537) ------------------------------------

  handleVisibility() {
    if (document.visibilityState === "hidden") {
      if (this.suspended || this.suspendTimer) return
      this.suspendTimer = setTimeout(() => this.suspend(), HIDDEN_GRACE_MS)
    } else {
      if (this.suspendTimer) clearTimeout(this.suspendTimer)
      this.suspendTimer = null
      if (this.suspended) this.resume()
    }
  }

  suspend() {
    this.suspendTimer = null
    if (this.suspended || !this.subscription) return
    this.suspended = true
    // unsubscribing broadcasts our `leave` so peers drop us from their roster.
    this.subscription.unsubscribe()
    this.subscription = null
    // Close the socket if nothing else is using it — unsubscribe alone leaves it open.
    releaseCableIfIdle()
    // Our own roster is stale until we rejoin; clear it so a hidden tab shows nothing.
    this.me = null
    this.clearRoster()
    this.render()
  }

  resume() {
    if (!this.suspended) return
    this.suspended = false
    // A fresh subscribe replays welcome + join/announce, rebuilding the roster.
    this.subscribe()
  }

  clearRoster() {
    this.carets.forEach((el) => el.remove())
    this.carets.clear()
    this.members.clear()
  }

  received(data) {
    switch (data.event) {
      case "welcome":
        this.me = data.you
        break
      case "join":
        if (this.isSelf(data.member)) break
        this.members.set(data.member.sid, data.member)
        // Let the newcomer learn we're here.
        this.subscription.perform("announce", {})
        this.render()
        break
      case "announce":
        if (this.isSelf(data.member)) break
        this.members.set(data.member.sid, data.member)
        this.render()
        break
      case "leave":
        this.members.delete(data.member.sid)
        this.removeCaret(data.member.sid)
        this.render()
        break
      case "cursor":
        if (this.collabValue || this.isSelf(data.member)) break
        if (!this.members.has(data.member.sid)) {
          this.members.set(data.member.sid, data.member)
          this.render()
        }
        this.drawCaret(data.member, data.caret)
        break
    }
  }

  isSelf(member) {
    return this.me && member.sid === this.me.sid
  }

  // ---- local caret reporting -------------------------------------------------

  scheduleCursor() {
    if (this.cursorTimer) return
    this.cursorTimer = setTimeout(() => {
      this.cursorTimer = null
      this.reportCursor()
    }, 120)
  }

  reportCursor() {
    if (!this.surface || !this.subscription) return
    try {
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      const range = sel.getRangeAt(0)
      if (!this.surface.contains(range.startContainer)) return
      const rect = range.getClientRects()[0] || range.getBoundingClientRect()
      const box = this.surface.getBoundingClientRect()
      // Coordinates in the surface's content space (scroll-independent), so they map
      // to the same logical spot for every client regardless of their scroll offset.
      this.subscription.perform("cursor", {
        top: Math.round(rect.top - box.top + this.surface.scrollTop),
        left: Math.round(rect.left - box.left + this.surface.scrollLeft),
        height: Math.round(rect.height) || 18,
        active: true
      })
    } catch {
      // Caret geometry is best-effort; the roster + banner still work without it.
    }
  }

  // ---- rendering -------------------------------------------------------------

  render() {
    const others = Array.from(this.members.values())
    if (this.hasBarTarget) {
      this.barTarget.replaceChildren(...others.map((m) => this.avatar(m)))
    }
    if (!this.collabValue && this.hasBannerTarget) {
      const n = others.length
      this.bannerTarget.classList.toggle("is-active", n > 0)
      if (n > 0) {
        const names = others.map((m) => m.name).join(", ")
        this.bannerTarget.textContent =
          `⚠ ${n === 1 ? names + " is" : n + " other people are"} editing this page right now — ` +
          `changes aren't merged yet, so saving may overwrite theirs.`
      }
    }
  }

  avatar(member) {
    const el = document.createElement("span")
    el.className = "fd-presence-avatar"
    el.style.backgroundColor = member.color
    el.textContent = member.initials || "?"
    el.title = member.name
    return el
  }

  drawCaret(member, caret) {
    if (!this.surface || !caret || caret.active === false) return this.removeCaret(member.sid)
    let el = this.carets.get(member.sid)
    if (!el) {
      el = document.createElement("div")
      el.className = "fd-presence-caret"
      const flag = document.createElement("span")
      flag.className = "fd-presence-caret-flag"
      flag.textContent = member.name
      flag.style.backgroundColor = member.color
      el.appendChild(flag)
      el.style.backgroundColor = member.color
      this.surface.appendChild(el)
      this.carets.set(member.sid, el)
    }
    el.style.top = `${caret.top}px`
    el.style.left = `${caret.left}px`
    el.style.height = `${caret.height}px`
  }

  removeCaret(sid) {
    const el = this.carets.get(sid)
    if (el) {
      el.remove()
      this.carets.delete(sid)
    }
  }
}
